import React, { useState, useEffect, useCallback } from "react";
import { api, apiFile, formatCurrency, formatDate, getReceiptUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import EmptyState from "../components/Help/EmptyState";
import DeductibilityStatusPill from "../components/DeductibilityStatusPill";
import TransactionDetailModal from "../components/TransactionDetailModal";
import {
  Flag, CheckCircle2, AlertCircle, Download, FileText,
  Building, Paperclip, Save, Edit3, Filter, RefreshCw,
  ExternalLink, ArrowUpRight, ArrowDownRight, Clock
} from "lucide-react";
import { toast } from "sonner";

export default function CpaReviewQueue() {
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");

  const [selectedTx, setSelectedTx] = useState(null);
  const [selectedCpaItem, setSelectedCpaItem] = useState(null);
  const [editingNotesId, setEditingNotesId] = useState(null);
  const [editNotesValue, setEditNotesValue] = useState("");
  const [resolvingId, setResolvingId] = useState(null);

  const { can } = useAuth();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter };
      if (propertyFilter !== "all") params.property_id = propertyFilter;
      if (yearFilter !== "all") params.year = parseInt(yearFilter, 10);

      const [cpaRes, propRes] = await Promise.all([
        api.get("/cpa-review", { params }),
        api.get("/properties"),
      ]);
      setItems(cpaRes.data || []);
      setProperties(propRes.data || []);
    } catch (err) {
      toast.error("Failed to load CPA review queue");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, propertyFilter, yearFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleResolveToggle = async (item, resolvedStatus) => {
    setResolvingId(item.id);
    try {
      await api.post(`/cpa-review/${item.id}/resolve`, {
        cpa_resolved: resolvedStatus,
        cpa_notes: item.cpa_notes,
      });
      toast.success(resolvedStatus ? "Item marked as resolved" : "Item reopened for review");
      loadData();
    } catch (err) {
      toast.error("Failed to update status");
    } finally {
      setResolvingId(null);
    }
  };

  const handleSaveNotes = async (item) => {
    try {
      await api.post(`/cpa-review/${item.id}/resolve`, {
        cpa_resolved: item.cpa_resolved,
        cpa_notes: editNotesValue,
      });
      toast.success("CPA notes saved");
      setEditingNotesId(null);
      loadData();
    } catch (err) {
      toast.error("Failed to save CPA notes");
    }
  };

  const handleUpdatePct = async (item, newPct) => {
    const pct = parseFloat(newPct);
    if (isNaN(pct) || pct < 0 || pct > 100) return;
    try {
      await api.post(`/cpa-review/${item.id}/resolve`, {
        cpa_resolved: item.cpa_resolved,
        cpa_notes: item.cpa_notes,
        deductible_pct: pct,
        deductibility_reason: `CPA adjusted to ${pct}% deduction`,
      });
      toast.success(`Deductible set to ${pct}%`);
      loadData();
    } catch (err) {
      toast.error("Failed to update deductible percentage");
    }
  };

  const handleExportCsv = async () => {
    try {
      const res = await apiFile.get("/csv/cpa-review", {
        params: {
          status: statusFilter,
          year: yearFilter !== "all" ? parseInt(yearFilter, 10) : undefined,
        },
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `cpa-review-queue-${statusFilter}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CPA Review CSV exported");
    } catch (err) {
      toast.error("Failed to export CSV");
    }
  };

  const handleExportPdf = async () => {
    try {
      const res = await apiFile.get("/pdf/cpa-review", {
        params: {
          status: statusFilter,
          year: yearFilter !== "all" ? parseInt(yearFilter, 10) : undefined,
        },
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `cpa-review-report-${statusFilter}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CPA Review PDF exported");
    } catch (err) {
      toast.error("Failed to export PDF");
    }
  };

  // Metrics
  const pendingItems = items.filter((i) => !i.cpa_resolved);
  const resolvedItems = items.filter((i) => i.cpa_resolved);
  const totalFlaggedAmount = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const pendingAmount = pendingItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

  return (
    <Layout
      title="CPA Review Queue"
      subtitle="Transactions flagged during bookkeeping entry for professional tax accountant determination"
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCsv}
            className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
            title="Download CSV for CPA"
          >
            <Download size={14} /> Export CSV
          </button>
          <button
            onClick={handleExportPdf}
            className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5"
            title="Generate CPA Review PDF Report"
          >
            <FileText size={14} /> Export PDF
          </button>
        </div>
      }
    >
      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="card p-4 flex items-center justify-between border-amber-500/25 bg-amber-50/30 dark:bg-amber-950/15">
          <div>
            <div className="text-xs text-amber-800 dark:text-amber-400 font-medium">Pending CPA Review</div>
            <div className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">
              {pendingItems.length} {pendingItems.length === 1 ? "Item" : "Items"}
            </div>
            <div className="text-[11px] text-[var(--muted)] mt-0.5">{formatCurrency(pendingAmount)} flagged</div>
          </div>
          <div className="p-2.5 rounded-xl bg-amber-100 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400">
            <Clock className="w-5 h-5" />
          </div>
        </div>

        <div className="card p-4 flex items-center justify-between border-emerald-500/20 bg-emerald-50/20 dark:bg-emerald-950/10">
          <div>
            <div className="text-xs text-emerald-800 dark:text-emerald-400 font-medium">Resolved Items</div>
            <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
              {resolvedItems.length} {resolvedItems.length === 1 ? "Item" : "Items"}
            </div>
            <div className="text-[11px] text-[var(--muted)] mt-0.5">Approved & classified</div>
          </div>
          <div className="p-2.5 rounded-xl bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="w-5 h-5" />
          </div>
        </div>

        <div className="card p-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-[var(--muted)] font-medium">Total Flagged Amount</div>
            <div className="text-2xl font-black text-[var(--text)] mt-1">
              {formatCurrency(totalFlaggedAmount)}
            </div>
            <div className="text-[11px] text-[var(--muted)] mt-0.5">Across selected filters</div>
          </div>
          <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500">
            <Flag className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-4">
        {/* Status segmented control */}
        <div className="segmented">
          {[
            { id: "pending", label: `Pending (${pendingItems.length})` },
            { id: "resolved", label: `Resolved (${resolvedItems.length})` },
            { id: "all", label: "All Flagged" },
          ].map((tab) => (
            <button
              key={tab.id}
              className={statusFilter === tab.id ? "active" : ""}
              onClick={() => setStatusFilter(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <select
            value={propertyFilter}
            onChange={(e) => setPropertyFilter(e.target.value)}
            className="lapen-input text-xs py-1.5 px-3 w-auto"
          >
            <option value="all">All Properties</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="lapen-input text-xs py-1.5 px-3 w-auto"
          >
            <option value="all">All Years</option>
            <option value="2025">2025</option>
            <option value="2024">2024</option>
            <option value="2023">2023</option>
          </select>
        </div>
      </div>

      {/* Items List */}
      <div className="card overflow-hidden p-0">
        {items.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon={CheckCircle2}
              title={statusFilter === "pending" ? "Zero Items Pending CPA Review" : "No Flagged Transactions"}
              description="All transactions are classified and resolved. Items flagged for ambiguous repair vs. improvement, personal allocation, or missing receipts will appear here."
              helpTopic="schedule-e"
              helpText="Learn about Schedule E Classification"
            />
          </div>
        ) : (
          <div className="table-responsive-container">
            {/* Desktop Table View (>= 700px) */}
            <div className="table-desktop-view overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-[var(--border)] bg-[var(--surface-hover)] text-[var(--muted)] font-bold uppercase tracking-wider text-[10.5px]">
                    <th className="p-3.5 pl-4">Date & Category</th>
                    <th className="p-3.5">Property & Vendor</th>
                    <th className="p-3.5 text-right">Gross Amount</th>
                    <th className="p-3.5 text-center">Deductible %</th>
                    <th className="p-3.5">Review Reason</th>
                    <th className="p-3.5">CPA Resolution Notes</th>
                    <th className="p-3.5 text-center">Status</th>
                    <th className="p-3.5 pr-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() => setSelectedCpaItem(item)}
                      className="hover:bg-[var(--surface-hover)]/70 transition-colors cursor-pointer group"
                      data-testid={`cpa-row-${item.id}`}
                    >
                      <td className="p-3.5 pl-4">
                        <div className="font-bold text-[var(--text)] group-hover:text-teal-400 transition-colors">
                          {item.category}
                        </div>
                        <div className="text-[11px] text-[var(--muted)] mt-0.5">{formatDate(item.date)}</div>
                      </td>

                      <td className="p-3.5">
                        <div className="font-medium text-[var(--text)]">{item.property_name || "Portfolio"}</div>
                        <div className="text-[11px] text-[var(--muted)] flex items-center gap-1.5 mt-0.5">
                          <span>{item.vendor_name || "—"}</span>
                          {item.receipt_path && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTx(item);
                              }}
                              className="text-teal-400 hover:text-teal-300 flex items-center gap-0.5"
                              title="View Receipt"
                            >
                              <Paperclip size={11} />
                              <span>Receipt</span>
                            </button>
                          )}
                        </div>
                      </td>

                      <td className="p-3.5 text-right font-black text-[var(--text)] tabular-nums font-mono whitespace-nowrap text-sm">
                        {formatCurrency(item.amount)}
                      </td>

                      <td className="p-3.5 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1">
                          <select
                            value={item.deductible_pct != null ? item.deductible_pct : 100}
                            onChange={(e) => handleUpdatePct(item, e.target.value)}
                            disabled={!can.edit}
                            className="lapen-input text-xs py-1 px-1.5 w-16 text-center font-bold"
                          >
                            <option value="100">100%</option>
                            <option value="50">50%</option>
                            <option value="0">0%</option>
                          </select>
                        </div>
                      </td>

                      <td className="p-3.5 text-amber-700 dark:text-amber-400 font-medium max-w-xs">
                        <div className="flex items-start gap-1">
                          <Flag size={12} className="shrink-0 mt-0.5 fill-amber-500 text-amber-500" />
                          <span className="leading-snug">{item.cpa_review_reason || item.deductibility_reason || "CPA review needed"}</span>
                        </div>
                      </td>

                      <td className="p-3.5 max-w-xs" onClick={(e) => e.stopPropagation()}>
                        {editingNotesId === item.id ? (
                          <div className="flex items-center gap-1.5">
                            <input
                              type="text"
                              value={editNotesValue}
                              onChange={(e) => setEditNotesValue(e.target.value)}
                              placeholder="Add CPA resolution notes..."
                              className="lapen-input text-xs py-1 px-2 flex-1"
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => handleSaveNotes(item)}
                              className="p-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shrink-0"
                              title="Save Notes"
                            >
                              <Save size={13} />
                            </button>
                          </div>
                        ) : (
                          <div
                            onClick={() => {
                              if (can.edit) {
                                setEditingNotesId(item.id);
                                setEditNotesValue(item.cpa_notes || "");
                              }
                            }}
                            className="cursor-pointer group flex items-center justify-between gap-1 text-[var(--muted)] hover:text-[var(--text)]"
                            title="Click to edit CPA notes"
                          >
                            <span className="italic truncate text-[11px]">
                              {item.cpa_notes || "Add note..."}
                            </span>
                            <Edit3 size={11} className="opacity-0 group-hover:opacity-100 shrink-0" />
                          </div>
                        )}
                      </td>

                      <td className="p-3.5 text-center whitespace-nowrap">
                        {item.cpa_resolved ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800/60">
                            <CheckCircle2 size={12} />
                            <span>Resolved</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-800/60">
                            <Clock size={12} />
                            <span>Pending</span>
                          </span>
                        )}
                      </td>

                      <td className="p-3.5 pr-4 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          {can.edit && (
                            <button
                              type="button"
                              onClick={() => handleResolveToggle(item, !item.cpa_resolved)}
                              disabled={resolvingId === item.id}
                              className={`px-2 py-1 rounded-lg text-xs font-semibold transition-colors ${
                                item.cpa_resolved
                                  ? "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200"
                                  : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs"
                              }`}
                            >
                              {item.cpa_resolved ? "Reopen" : "Mark Resolved"}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setSelectedTx(item)}
                            className="p-1.5 rounded-lg text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)]"
                            title="View Full Details"
                          >
                            <ExternalLink size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards View (< 700px) according to "The Six Moves" */}
            <div className="table-cards-view divide-y divide-[var(--border)]">
              {items.map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedCpaItem(item)}
                  className="thumb-row hover:bg-[var(--surface-hover)] active:bg-[var(--surface-hover)] transition-colors group cursor-pointer select-none"
                  data-testid={`cpa-card-${item.id}`}
                  role="button"
                  tabIndex={0}
                >
                  {/* Line 1: Identity (Left) & Value (Top Right Slot) */}
                  <div className="flex items-baseline justify-between gap-3 min-w-0">
                    <span className="font-bold text-[15px] text-[var(--text)] group-hover:text-teal-400 transition-colors truncate min-w-0">
                      {item.category}
                    </span>
                    {/* Move 3: ONE SLOT FOR THE AMOUNT - Top right, tabular figures */}
                    <span className="card-amount-slot text-[16px] text-teal-400">
                      {formatCurrency(item.amount)}
                    </span>
                  </div>

                  {/* Line 2: State & Property (Left) & Labeled Flag Reason (Right) */}
                  <div className="flex items-center justify-between gap-3 text-xs mt-1.5 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0 truncate">
                      <span className="text-[var(--text)] truncate font-medium">
                        {item.property_name || "Portfolio"}
                      </span>
                      <span className="text-[var(--border)]">·</span>
                      <span
                        className={`tag py-0 px-1.5 text-[10px] shrink-0 ${
                          item.cpa_resolved ? "tag-success" : "tag-warning"
                        }`}
                      >
                        {item.cpa_resolved ? "Resolved" : "Pending"}
                      </span>
                      <span className="text-[10px] text-[var(--muted)] shrink-0">
                        {item.deductible_pct != null ? item.deductible_pct : 100}%
                      </span>
                    </div>

                    {/* Move 4: Label the ambiguous */}
                    <div className="text-amber-500 font-medium shrink-0 whitespace-nowrap text-[11px] truncate max-w-[130px]">
                      Flag: {item.cpa_review_reason || item.deductibility_reason || "CPA review"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Move 5: PROGRESSIVE DISCLOSURE VIA BOTTOM SHEET FOR CPA REVIEW */}
      {selectedCpaItem && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-150"
          onClick={() => setSelectedCpaItem(null)}
          data-testid="cpa-detail-modal"
        >
          <div
            className="card w-full max-w-lg p-0 overflow-hidden shadow-2xl rounded-t-3xl sm:rounded-3xl max-h-[90vh] flex flex-col my-0 sm:my-auto animate-in slide-in-from-bottom-4 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile Drawer Pull Handle */}
            <div className="sm:hidden pt-3 pb-1 flex justify-center">
              <div className="w-12 h-1.5 rounded-full bg-slate-300 dark:bg-slate-700" />
            </div>

            {/* Header */}
            <div className="p-5 border-b border-[var(--border)] flex items-start justify-between bg-[var(--surface-hover)]">
              <div className="min-w-0 pr-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-teal-400">
                    CPA Review Item
                  </span>
                  <span
                    className={`tag text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      selectedCpaItem.cpa_resolved ? "tag-success" : "tag-warning"
                    }`}
                  >
                    {selectedCpaItem.cpa_resolved ? "Resolved" : "Pending Review"}
                  </span>
                </div>
                <h3 className="text-xl font-extrabold text-[var(--text)] mt-1 truncate">
                  {selectedCpaItem.category}
                </h3>
                <p className="text-xs text-[var(--muted)] mt-0.5 truncate">
                  {selectedCpaItem.property_name || "Portfolio"} · {selectedCpaItem.vendor_name || "Unassigned Vendor"} · {formatDate(selectedCpaItem.date)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedCpaItem(null)}
                className="p-1.5 rounded-xl text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface)] transition-colors shrink-0"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4 overflow-y-auto">
              {/* Gross Amount Hero */}
              <div className="p-4 rounded-2xl bg-[var(--surface)] border border-[var(--border)] flex items-center justify-between">
                <div>
                  <div className="text-xs text-[var(--muted)] font-medium">Gross Transaction Amount</div>
                  <div className="text-2xl font-black text-[var(--text)] tabular-nums font-mono mt-0.5">
                    {formatCurrency(selectedCpaItem.amount)}
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-xs text-[var(--muted)] font-medium">Deductibility %</div>
                  <div className="mt-1">
                    <select
                      value={selectedCpaItem.deductible_pct != null ? selectedCpaItem.deductible_pct : 100}
                      onChange={(e) => {
                        const val = e.target.value;
                        handleUpdatePct(selectedCpaItem, val);
                        setSelectedCpaItem((prev) => ({ ...prev, deductible_pct: parseInt(val, 10) }));
                      }}
                      disabled={!can.edit}
                      className="lapen-input text-xs py-1 px-2 font-bold w-20 text-center"
                    >
                      <option value="100">100%</option>
                      <option value="50">50%</option>
                      <option value="0">0%</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Review Flag Reason */}
              <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/25 text-xs text-amber-700 dark:text-amber-300">
                <div className="flex items-center gap-1.5 font-bold mb-1">
                  <Flag size={13} className="text-amber-500 fill-amber-500" />
                  <span>Review Trigger Reason</span>
                </div>
                <p className="leading-relaxed">
                  {selectedCpaItem.cpa_review_reason || selectedCpaItem.deductibility_reason || "CPA review flagged for classification verification."}
                </p>
              </div>

              {/* CPA Notes */}
              <div className="p-3.5 rounded-xl bg-[var(--surface)] border border-[var(--border)] space-y-2">
                <label className="text-[11px] font-semibold text-[var(--muted)] block">
                  CPA Resolution Notes
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    defaultValue={selectedCpaItem.cpa_notes || ""}
                    placeholder="Add CPA guidance or resolution reasoning..."
                    className="lapen-input text-xs py-1.5 px-3 flex-1"
                    id={`cpa-notes-input-${selectedCpaItem.id}`}
                  />
                  {can.edit && (
                    <button
                      type="button"
                      onClick={() => {
                        const input = document.getElementById(`cpa-notes-input-${selectedCpaItem.id}`);
                        if (input) {
                          setEditNotesValue(input.value);
                          handleSaveNotes({ ...selectedCpaItem, cpa_notes: input.value });
                          setSelectedCpaItem((prev) => ({ ...prev, cpa_notes: input.value }));
                        }
                      }}
                      className="btn-primary text-xs py-1.5 px-3 shrink-0"
                    >
                      <Save size={13} className="mr-1 inline" /> Save Note
                    </button>
                  )}
                </div>
              </div>

              {/* Attached Receipt link */}
              {selectedCpaItem.receipt_path && (
                <div className="flex items-center justify-between p-3 rounded-xl bg-teal-500/10 border border-teal-500/20 text-xs text-teal-400">
                  <div className="flex items-center gap-1.5 font-medium">
                    <Paperclip size={14} />
                    <span>Receipt document attached</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const item = selectedCpaItem;
                      setSelectedCpaItem(null);
                      setSelectedTx(item);
                    }}
                    className="text-xs font-bold text-teal-300 hover:underline flex items-center gap-1"
                  >
                    <span>View Receipt</span>
                    <ExternalLink size={12} />
                  </button>
                </div>
              )}
            </div>

            {/* Footer with Actions */}
            <div className="p-4 border-t border-[var(--border)] bg-[var(--surface-hover)] flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setSelectedCpaItem(null)}
                className="btn-secondary text-xs py-2 px-4"
              >
                Close
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const item = selectedCpaItem;
                    setSelectedCpaItem(null);
                    setSelectedTx(item);
                  }}
                  className="btn-secondary text-xs py-2 px-3 inline-flex items-center gap-1"
                >
                  <ExternalLink size={13} /> Full Record
                </button>

                {can.edit && (
                  <button
                    type="button"
                    onClick={() => {
                      const item = selectedCpaItem;
                      const nextState = !item.cpa_resolved;
                      handleResolveToggle(item, nextState);
                      setSelectedCpaItem((prev) => ({ ...prev, cpa_resolved: nextState }));
                    }}
                    disabled={resolvingId === selectedCpaItem.id}
                    className={`btn-primary text-xs py-2 px-4 ${
                      selectedCpaItem.cpa_resolved
                        ? "!bg-slate-700 hover:!bg-slate-600 text-white"
                        : "!bg-emerald-600 hover:!bg-emerald-500 text-white"
                    }`}
                  >
                    {selectedCpaItem.cpa_resolved ? "Reopen for Review" : "Resolve & Approve"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Detail & Receipt Modal */}
      {selectedTx && (
        <TransactionDetailModal
          transaction={selectedTx}
          onClose={() => setSelectedTx(null)}
          onUpdate={(updated) => {
            setSelectedTx(updated);
            loadData();
          }}
          properties={properties}
        />
      )}
    </Layout>
  );
}
